import type { NotificationCategory, UnreadCounts } from '@/api/notificationsApi'

/**
 * Rolls unread notification counts up into per-destination side-nav badges.
 *
 * Like notificationLink, the route knowledge lives HERE rather than in the
 * backend: the backend counts by category, and the frontend alone decides which
 * categories a given nav destination speaks for. A new category therefore never
 * needs a backend change to start badging.
 *
 * This module is pure (no React/network imports) so the rollup can be unit
 * tested directly. The badge markup itself has no test coverage, as vitest runs
 * in the node environment here.
 */

// Which notification categories roll up into which side-nav destination.
// Add a line here as each module grows a notification trigger.
//
// Keys are EXACT paths, not prefixes: /procedures/calendar ("Upcoming
// Procedures") is its own destination and deliberately gets no badge.
const NAV_BADGE_CATEGORIES: Partial<Record<string, readonly NotificationCategory[]>> = {
  '/patients': ['PATIENT_INTAKE'],
  // A LIS result waiting for approval belongs to a transplant.
  '/procedures': ['PROCEDURE_DUE', 'LIS_RESULT_REVIEW'],
}

export function navBadgeCount(to: string, counts: UnreadCounts | undefined): number {
  if (!counts) return 0
  if (to === '/notifications') return counts.count // the total, every category
  return (NAV_BADGE_CATEGORIES[to] ?? []).reduce((sum, c) => sum + (counts.byCategory[c] ?? 0), 0)
}
